"use client";

import Autoplay from "embla-carousel-autoplay";
import useEmblaCarousel from "embla-carousel-react";
import { useCallback, useEffect, useState } from "react";
import { borders, spacing, typography } from "@/styles/design-tokens";
import Video from "./Video";

const caseStudies = [
  {
    _id: 1,
    client: 'Atlanime Fest',
    link: 'https://www.atlanimefest.com',
    playbackId: 'cqwtBbX6RyCKCH01aFKaYzF016FMOv1KNI00y2rW01bzcqU',
    description: 'Designed and built a custom website and CMS for Atlanta’s #1 Anime & Music Festival, capturing the energy of their brand with vibrant visuals and playful microinteractions.'
  },
  {
    _id: 2,
    client: 'Milk & Cookies Festival',
    link: 'https://milkandcookiesfestival.com/',
    playbackId: 'HbGePnUsTdqK008YeJ5YedAM802XKENbcUajhjsto3QvI',
    description: 'Designed and built the official website for Milk and Cookies Festival as they launched their groundbreaking South Africa show. The site celebrates this vibrant music and culture event, showcasing dynamic lineups, immersive experiences, and connecting fans worldwide.'
  },
  {
    _id: 3,
    client: 'HBCUs Outside',
    link: 'https://www.hbcusoutside.com/',
    playbackId: 'DN7X1ZG029XOPWEcdFyoP97xLIgImupp8RV102LX3E6A4',
    description: 'HBCUs Outside is a nonprofit connecting HBCU students, alumni, and supporters to outdoor activities and community events. Their mission is to make it easy to discover, join, and participate in experiences that go beyond campus life.'
  },
  {
    _id: 4,
    client: 'MCMW',
    link: 'https://www.mcmw.global/',
    playbackId: 'xmiy80001R600MuDYov1xAmVDT01D5Qqi3D00iEpWoAQkmhU',
    description: 'Developed a high-impact landing page for Milk & Cookies Music Week, a festival celebrating music, culture, and community. Built for speed, responsiveness, and a standout visual experience.'
  },
  {
    _id: 5,
    client: 'Hike Clerb',
    link: 'https://www.hikeclerb.org/',
    playbackId: 'hV01EeVmhW91ZWud5eJy6003021TCYfp41zyHGN7nnKvHI',
    description: 'An online shop for Hike Clerb, a community-driven nonprofit reimagining the outdoors as an inclusive and equitable space. The site blends commerce, storytelling, and accessibility to support their mission.'
  },
  
  {
    _id: 6,
    client: "Ethel's Club",
    link: 'https://heroic-mandazi-a847d9.netlify.app/',
    playbackId: 'E8KCYBVNJB1J7iIxp01mvyJrFUWeXr00wk3UXT6O2U4Ug',
    description: 'An ethical eCommerce store for a cultural and wellness platform.'
  },
]

export function CaseStudyCarousel() {
	const [emblaRef, emblaApi] = useEmblaCarousel(
		{
			loop: true,
			slidesToScroll: 1,
			dragFree: false,
			containScroll: "trimSnaps",
		},
		[Autoplay({ delay: 10000, stopOnInteraction: false })],
	);

	const [prevBtnEnabled, setPrevBtnEnabled] = useState(false);
	const [nextBtnEnabled, setNextBtnEnabled] = useState(false);

	const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
	const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

	const onSelect = useCallback(() => {
		if (!emblaApi) return;
		setPrevBtnEnabled(emblaApi.canScrollPrev());
		setNextBtnEnabled(emblaApi.canScrollNext());
	}, [emblaApi]);

	useEffect(() => {
		if (!emblaApi) return;
		onSelect();
		emblaApi.on("select", onSelect);
		emblaApi.on("reInit", onSelect);
	}, [emblaApi, onSelect]);

	if (caseStudies.length === 0) {
	  return null;
	}

	return (
		<section className={spacing.section}>
      <div className={`${spacing.container} !px-0 !max-w-full`}>
        <div className="mx-auto flex flex-col gap-3 sm:text-center">
          <h2 className={typography.h2}>Our Work</h2>
          <div className="embla__buttons flex items-center justify-center gap-4">
            <button
              type="button"
              onClick={scrollPrev}
              disabled={!prevBtnEnabled}
              aria-label="Previous slide"
              aria-disabled={!prevBtnEnabled}
              className={`${typography.h5} uppercase cursor-pointer hover:underline disabled:opacity-50`}
            >
              Prev
            </button>
            <button
              type="button"
              onClick={scrollNext}
              disabled={!nextBtnEnabled}
              aria-label="Next slide"
              aria-disabled={!nextBtnEnabled}
              className={`${typography.h5} uppercase cursor-pointer hover:underline disabled:opacity-50`}
            >
              Next
            </button>
          </div>
        </div>
        <div className="embla overflow-hidden" ref={emblaRef}>
          <div className="embla__container flex items-start hover:cursor-grab">
            {caseStudies.map((study) => {
              const CardContent = (
                <>
                  <div
                    className={`${borders.md} relative overflow-hidden aspect-video w-full bg-black transition-all duration-300 ease-out group-hover:-translate-x-[3px] group-hover:-translate-y-[3px] group-hover:shadow-[4px_4px_0px_rgba(4,6,2,0.25)] `}
                  >
                    {study.playbackId && (
                      <Video
                        playbackId={study.playbackId}
                        client={study.client}
                      />
                    )}
                    <p
                      className={`${typography.bodySmall} rounded-full border md:opacity-0 absolute bottom-4 right-4 z-10 bg-primary text-black py-2 px-4 group-hover:opacity-100 transition-all ease-in-out hover:bg-gray`}
                    >
                      View Website
                    </p>
                  </div>

                  <div className="mt-2 px-2 sm:mt-4 sm:px-4">
                    <h3 className={`${typography.h4} group-hover:underline`}>{study.client}</h3>
                    <p className={`${typography.body} mt-1`}>
                      {study.description}
                    </p>
                  </div>
                </>
              );

              return (
                <div
                  key={study._id}
                  className="embla__slide relative py-4 px-3 w-auto flex items-center justify-center flex-shrink-0 flex-grow-0 basis-[80%] md:px-6 md:basis-[50%] 2xl:basis-[40%]"
                >
                  <a
                    href={study.link || "#"}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full h-fit hover:opacity-100 transition-all group"
                  >
                    {CardContent}
                  </a>
                </div>
              );
            })}
          </div>
        </div>
      </div>
		</section>
	);
}
